import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';

const BOARDS = ['CBSE', 'ICSE', 'State', 'International'];
const TYPES  = ['Notes', 'Practice Questions', 'Previous Year Papers', 'Textbooks'];
const CLASSES = ['Class 1','Class 2','Class 3','Class 4','Class 5','Class 6','Class 7','Class 8','Class 9','Class 10','Class 11','Class 12'];

const STATUS_COLOR = { pending:'#f59e0b', fulfilled:'#16a34a', rejected:'#dc2626' };

function fmt_size(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/(1024*1024)).toFixed(1)} MB`;
}

function MaterialCard({ m, onBookmark, onDownload, onDelete }) {
  const [dlLoading, setDlLoading] = useState(false);
  const download = async () => {
    setDlLoading(true);
    await onDownload(m);
    setDlLoading(false);
  };
  return (
    <div style={{ border:'1.5px solid #e2e8f0', borderRadius:12, padding:'16px 18px', background:'#fff', display:'flex', flexDirection:'column', gap:10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:14, color:'#0f1f45', marginBottom:4, wordBreak:'break-word' }}>{m.title}</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
            <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:'#eff6ff', color:'#2554b8' }}>{m.class_name}</span>
            <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:'#f0fdf4', color:'#16a34a' }}>{m.board}</span>
            <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:'#fdf4ff', color:'#7c3aed' }}>{m.subject}</span>
            <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:'#fff7ed', color:'#c2410c' }}>{m.material_type}</span>
            {m.is_link && <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, background:'#ecfeff', color:'#0891b2' }}>🔗 Link</span>}
          </div>
        </div>
        {onBookmark && (
          <button onClick={() => onBookmark(m)} title={m.bookmarked ? 'Remove bookmark' : 'Bookmark'}
            style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color: m.bookmarked ? '#f59e0b' : '#cbd5e1', flexShrink:0, padding:0 }}>
            {m.bookmarked ? '★' : '☆'}
          </button>
        )}
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
        <span style={{ fontSize:11, color:'#94a3b8' }}>
          {m.file_size ? fmt_size(m.file_size) + ' · ' : ''}{m.download_count || 0} {m.is_link ? 'opens' : 'downloads'}
        </span>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={download} disabled={dlLoading}
            style={{ padding:'7px 18px', borderRadius:8, border:'none', background: dlLoading ? '#94a3b8' : m.is_link ? 'linear-gradient(135deg,#0891b2,#06b6d4)' : 'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, fontSize:13, cursor: dlLoading ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', gap:6 }}>
            {dlLoading ? '⏳ Opening…' : m.is_link ? '🔗 Open Link' : '⬇️ Download'}
          </button>
          {onDelete && (
            <button onClick={() => onDelete(m)}
              style={{ padding:'7px 14px', borderRadius:8, border:'1.5px solid #fca5a5', background:'#fff', color:'#dc2626', fontWeight:700, fontSize:13, cursor:'pointer' }}>
              🗑️ Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Admin upload + manage ─────────────────────────────────────────────────────
function AdminMaterials() {
  const [tab, setTab] = useState('library');
  const [materials, setMaterials] = useState([]);
  const [requests, setRequests] = useState([]);
  const [filters, setFilters] = useState({ class_name:'', board:'', subject:'', material_type:'', search:'' });
  const [subjectInput, setSubjectInput] = useState('');
  const [form, setForm] = useState({ title:'', class_name:'', board:'', subject:'', material_type:'', file:null, url:'' });
  const [addMode, setAddMode] = useState('file'); // 'file' | 'link'
  const [msg, setMsg] = useState('');
  const [uploading, setUploading] = useState(false);

  const addLink = async () => {
    if (!form.title.trim() || !form.class_name || !form.board || !form.subject.trim() || !form.material_type || !form.url.trim()) {
      setMsg('Please fill all fields and paste a link.'); return;
    }
    const url = form.url.trim();
    if (!/^https?:\/\//i.test(url)) { setMsg('Link must start with http:// or https://'); return; }
    setUploading(true); setMsg('');
    try {
      await API.post('/materials/link', {
        title: form.title.trim(), class_name: form.class_name, board: form.board,
        subject: form.subject.trim(), material_type: form.material_type, url,
      });
      setMsg('✅ Link added successfully!');
      setForm({ title:'', class_name:'', board:'', subject:'', material_type:'', file:null, url:'' });
      loadMaterials();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed to add link'); }
    finally { setUploading(false); }
  };

  const loadMaterials = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k,v]) => { if (v) p.set(k, v); });
    API.get(`/materials/?${p}`).then(r => setMaterials(r.data)).catch(() => {});
  }, [filters]);

  const loadRequests = useCallback(() => {
    API.get('/materials/requests').then(r => setRequests(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadMaterials(); }, [loadMaterials]);
  useEffect(() => { if (tab === 'requests') loadRequests(); }, [tab, loadRequests]);

  const upload = async () => {
    if (!form.title.trim() || !form.class_name || !form.board || !form.subject.trim() || !form.material_type || !form.file) {
      setMsg('Please fill all fields and select a PDF file.'); return;
    }
    if (!form.file.name.toLowerCase().endsWith('.pdf')) { setMsg('Only PDF files are accepted.'); return; }
    setUploading(true); setMsg('');
    const fd = new FormData();
    fd.append('title', form.title.trim());
    fd.append('class_name', form.class_name);
    fd.append('board', form.board);
    fd.append('subject', form.subject.trim());
    fd.append('material_type', form.material_type);
    fd.append('file', form.file);
    try {
      await API.post('/materials/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg('✅ Material uploaded successfully!');
      setForm({ title:'', class_name:'', board:'', subject:'', material_type:'', file:null });
      document.getElementById('mat-file-input').value = '';
      loadMaterials();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const deleteMaterial = async (m) => {
    if (!window.confirm(`Delete "${m.title}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/materials/${m.id}`);
      setMsg('✅ Deleted');
      loadMaterials();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Delete failed'); }
  };

  const download = async (m) => {
    // Link materials: open the external URL in a new tab (and count the click).
    if (m.is_link) {
      if (m.link_url) window.open(m.link_url, '_blank', 'noopener,noreferrer');
      API.post(`/materials/${m.id}/click`).catch(() => {});
      return;
    }
    try {
      const response = await API.get(`/materials/${m.id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = m.file_name || 'material.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Could not download file. Please try again.'); }
  };

  const updateRequestStatus = async (id, status) => {
    try {
      await API.patch(`/materials/requests/${id}?status=${status}`);
      loadRequests();
    } catch (e) { alert('Failed to update'); }
  };

  return (
    <div style={{ padding:'28px 32px' }}>
      <h2 style={{ fontWeight:800, fontSize:22, color:'#0f1f45', marginBottom:6 }}>📚 Study Materials</h2>
      <p style={{ color:'#64748b', fontSize:13, marginBottom:24 }}>Upload and manage study materials for students and companions</p>

      <div style={{ display:'flex', gap:0, marginBottom:24, background:'#f0f4fb', borderRadius:12, padding:4, width:'fit-content', border:'1px solid #e2e8f6' }}>
        {[['library','📂 Library'],['upload','⬆️ Upload'],['requests','📋 Requests']].map(([t,l]) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding:'8px 20px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:700, fontSize:13, background: tab===t ? '#fff' : 'transparent', color: tab===t ? '#2554b8' : '#64748b', boxShadow: tab===t ? '0 2px 8px rgba(37,84,184,0.12)' : 'none', transition:'all 0.2s' }}>{l}</button>
        ))}
      </div>

      {msg && <div style={{ padding:'10px 16px', borderRadius:8, fontSize:13, fontWeight:600, marginBottom:16, background: msg.startsWith('✅') ? '#f0fdf4' : '#fef2f2', color: msg.startsWith('✅') ? '#16a34a' : '#dc2626', border:`1px solid ${msg.startsWith('✅') ? '#86efac' : '#fca5a5'}`, cursor:'pointer' }} onClick={() => setMsg('')}>{msg}</div>}

      {tab === 'upload' && (
        <div style={{ background:'#fff', borderRadius:16, padding:28, border:'1.5px solid #e2e8f0', maxWidth:560 }}>
          <h3 style={{ fontWeight:800, fontSize:16, marginBottom:16, color:'#0f1f45' }}>Add New Material</h3>
          {/* Mode toggle: upload a PDF, or just link an external file */}
          <div style={{ display:'flex', gap:8, marginBottom:20, background:'#f1f5f9', padding:4, borderRadius:10, width:'fit-content' }}>
            {[['file','⬆️ Upload PDF'],['link','🔗 Add Link']].map(([m,label]) => (
              <button key={m} onClick={() => { setAddMode(m); setMsg(''); }}
                style={{ padding:'7px 16px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:700, fontSize:13,
                  background: addMode===m ? '#fff' : 'transparent', color: addMode===m ? '#2554b8' : '#64748b',
                  boxShadow: addMode===m ? '0 2px 8px rgba(37,84,184,0.12)' : 'none' }}>{label}</button>
            ))}
          </div>
          {addMode === 'link' && (
            <div style={{ padding:'10px 14px', marginBottom:14, borderRadius:10, background:'#ecfeff', border:'1px solid #a5f3fc', color:'#0e7490', fontSize:12.5, lineHeight:1.5 }}>
              Best for publicly-available books (e.g. SCERT / NCERT). Paste the direct link — students & companions open it straight from the source. Nothing is stored on our server.
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
            <div style={{ gridColumn:'1/-1' }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>TITLE *</label>
              <input className="form-control" value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Class 10 Maths Notes Chapter 1" maxLength={200} />
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>CLASS *</label>
              <select className="form-control" value={form.class_name} onChange={e => setForm(f=>({...f,class_name:e.target.value}))}>
                <option value="">— Select —</option>
                {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>BOARD *</label>
              <select className="form-control" value={form.board} onChange={e => setForm(f=>({...f,board:e.target.value}))}>
                <option value="">— Select —</option>
                {BOARDS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>SUBJECT *</label>
              <input className="form-control" value={form.subject} onChange={e => setForm(f=>({...f,subject:e.target.value}))} placeholder="e.g. Mathematics" maxLength={100} />
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>MATERIAL TYPE *</label>
              <select className="form-control" value={form.material_type} onChange={e => setForm(f=>({...f,material_type:e.target.value}))}>
                <option value="">— Select —</option>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {addMode === 'file' ? (
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>PDF FILE * (max 50 MB)</label>
                <input id="mat-file-input" type="file" accept=".pdf,application/pdf" className="form-control"
                  onChange={e => setForm(f=>({...f,file:e.target.files[0]||null}))} />
                {form.file && <span style={{ fontSize:11, color:'#64748b', marginTop:4, display:'block' }}>{form.file.name} · {fmt_size(form.file.size)}</span>}
              </div>
            ) : (
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:5 }}>LINK (URL) *</label>
                <input className="form-control" value={form.url} onChange={e => setForm(f=>({...f,url:e.target.value}))}
                  placeholder="https://scert.kerala.gov.in/.../textbook.pdf" maxLength={2000} />
              </div>
            )}
          </div>
          {addMode === 'file' ? (
            <button onClick={upload} disabled={uploading} style={{ width:'100%', padding:'12px', borderRadius:10, border:'none', background: uploading ? '#94a3b8' : 'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, fontSize:14, cursor: uploading ? 'not-allowed' : 'pointer' }}>
              {uploading ? '⏳ Uploading…' : '⬆️ Upload to Library'}
            </button>
          ) : (
            <button onClick={addLink} disabled={uploading} style={{ width:'100%', padding:'12px', borderRadius:10, border:'none', background: uploading ? '#94a3b8' : 'linear-gradient(135deg,#0891b2,#06b6d4)', color:'#fff', fontWeight:700, fontSize:14, cursor: uploading ? 'not-allowed' : 'pointer' }}>
              {uploading ? '⏳ Adding…' : '🔗 Add Link to Library'}
            </button>
          )}
        </div>
      )}

      {tab === 'library' && (
        <>
          <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20 }}>
            <input className="form-control" placeholder="🔍 Search materials…" value={filters.search} onChange={e => setFilters(f=>({...f,search:e.target.value}))} style={{ width:200 }} />
            <select className="form-control" value={filters.class_name} onChange={e => setFilters(f=>({...f,class_name:e.target.value}))} style={{ width:120 }}>
              <option value="">All Classes</option>
              {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="form-control" value={filters.board} onChange={e => setFilters(f=>({...f,board:e.target.value}))} style={{ width:100 }}>
              <option value="">All Boards</option>
              {BOARDS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <input className="form-control" placeholder="Subject…" value={subjectInput}
              onChange={e => setSubjectInput(e.target.value)}
              onBlur={() => setFilters(f=>({...f,subject:subjectInput.trim()}))}
              onKeyDown={e => e.key==='Enter' && setFilters(f=>({...f,subject:subjectInput.trim()}))}
              style={{ width:130 }} />
            <select className="form-control" value={filters.material_type} onChange={e => setFilters(f=>({...f,material_type:e.target.value}))} style={{ width:165 }}>
              <option value="">All Types</option>
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={() => { setFilters({ class_name:'', board:'', subject:'', material_type:'', search:'' }); setSubjectInput(''); }} style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #e2e8f0', background:'#fff', color:'#64748b', fontWeight:600, fontSize:13, cursor:'pointer' }}>Clear</button>
          </div>
          <p style={{ fontSize:13, color:'#64748b', marginBottom:14 }}>{materials.length} material{materials.length!==1?'s':''} found</p>
          {materials.length === 0
            ? <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}><div style={{ fontSize:48, marginBottom:12 }}>📂</div><p>No materials found. Upload some above.</p></div>
            : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14 }}>
                {materials.map(m => (
                  <MaterialCard key={m.id} m={m} onBookmark={null} onDownload={download} onDelete={deleteMaterial} />
                ))}
              </div>
          }
        </>
      )}

      {tab === 'requests' && (
        <div>
          <h3 style={{ fontWeight:700, fontSize:15, marginBottom:16, color:'#0f1f45' }}>Material Requests ({requests.length})</h3>
          {requests.length === 0
            ? <div style={{ textAlign:'center', padding:40, color:'#94a3b8' }}><div style={{ fontSize:40 }}>📋</div><p>No requests yet</p></div>
            : <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {requests.map(r => (
                  <div key={r.id} style={{ border:'1.5px solid #e2e8f0', borderRadius:12, padding:'14px 18px', background:'#fff', display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:14, color:'#0f1f45', marginBottom:4 }}>{r.title}</div>
                      <div style={{ fontSize:12, color:'#64748b', marginBottom:4 }}>
                        {[r.class_name, r.board, r.subject, r.material_type].filter(Boolean).join(' · ')}
                      </div>
                      {r.message && <div style={{ fontSize:12, color:'#475569', fontStyle:'italic' }}>"{r.message}"</div>}
                      <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>{r.requester_name} ({r.requester_role}) · {new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, background: r.status==='fulfilled' ? '#dcfce7' : r.status==='rejected' ? '#fee2e2' : '#fef9c3', color: STATUS_COLOR[r.status] }}>{r.status}</span>
                      {r.status === 'pending' && <>
                        <button onClick={() => updateRequestStatus(r.id, 'fulfilled')} style={{ padding:'4px 12px', borderRadius:6, border:'none', background:'#16a34a', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>✓ Fulfilled</button>
                        <button onClick={() => updateRequestStatus(r.id, 'rejected')} style={{ padding:'4px 12px', borderRadius:6, border:'none', background:'#dc2626', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>✗ Reject</button>
                      </>}
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      )}
    </div>
  );
}

// ── Student / Tutor view ──────────────────────────────────────────────────────
function UserMaterials({ role }) {
  const [tab, setTab] = useState('browse');
  const [materials, setMaterials] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [recent, setRecent] = useState([]);
  const [requests, setRequests] = useState([]);
  const [filters, setFilters] = useState({ class_name:'', board:'', subject:'', material_type:'', search:'' });
  const [subjectInput, setSubjectInput] = useState('');
  const [reqForm, setReqForm] = useState({ title:'', class_name:'', board:'', subject:'', material_type:'', message:'' });
  const [msg, setMsg] = useState('');
  const [reqLoading, setReqLoading] = useState(false);

  const loadMaterials = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k,v]) => { if (v) p.set(k, v); });
    API.get(`/materials/?${p}`).then(r => setMaterials(r.data)).catch(() => {});
  }, [filters]);

  useEffect(() => { loadMaterials(); }, [loadMaterials]);
  useEffect(() => {
    API.get('/materials/bookmarks/mine').then(r => setBookmarks(r.data)).catch(() => {});
    API.get('/materials/recently-viewed/mine').then(r => setRecent(r.data)).catch(() => {});
    API.get('/materials/requests').then(r => setRequests(r.data)).catch(() => {});
  }, []);

  const toggleBookmark = async (m) => {
    try {
      const r = await API.post(`/materials/${m.id}/bookmark`);
      const update = (list) => list.map(x => x.id === m.id ? {...x, bookmarked: r.data.bookmarked} : x);
      setMaterials(update);
      if (r.data.bookmarked) {
        setBookmarks(prev => [{...m, bookmarked:true}, ...prev.filter(x=>x.id!==m.id)]);
      } else {
        setBookmarks(prev => prev.filter(x => x.id !== m.id));
      }
    } catch {}
  };

  const download = async (m) => {
    try {
      const response = await API.get(`/materials/${m.id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = m.file_name || 'material.pdf';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
      setMaterials(prev => prev.map(x => x.id===m.id ? {...x, download_count:(x.download_count||0)+1} : x));
      setRecent(prev => [{...m}, ...prev.filter(x=>x.id!==m.id)].slice(0,10));
    } catch (e) { alert('Could not download file. Please try again.'); }
  };

  const submitRequest = async () => {
    if (!reqForm.title.trim()) { setMsg('Please enter a title for your request'); return; }
    setReqLoading(true);
    try {
      await API.post('/materials/requests', { ...reqForm, title: reqForm.title.trim() });
      setMsg('✅ Request submitted! Admin will review and upload the material.');
      setReqForm({ title:'', class_name:'', board:'', subject:'', material_type:'', message:'' });
      API.get('/materials/requests').then(r => setRequests(r.data)).catch(() => {});
    } catch (e) { setMsg(e?.response?.data?.detail || 'Failed to submit'); }
    finally { setReqLoading(false); }
  };

  return (
    <div style={{ padding:'28px 32px' }}>
      <h2 style={{ fontWeight:800, fontSize:22, color:'#0f1f45', marginBottom:6 }}>📚 Study Materials</h2>
      <p style={{ color:'#64748b', fontSize:13, marginBottom:24 }}>Browse, download and bookmark study materials</p>

      <div style={{ display:'flex', gap:0, marginBottom:24, background:'#f0f4fb', borderRadius:12, padding:4, width:'fit-content', border:'1px solid #e2e8f6', flexWrap:'wrap' }}>
        {[['browse','📂 Browse'],['bookmarks',`★ Bookmarks (${bookmarks.length})`],['recent','🕐 Recently Viewed'],['requests','📋 My Requests']].map(([t,l]) => (
          <button key={t} onClick={() => { setTab(t); setMsg(''); }} style={{ padding:'8px 18px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:700, fontSize:12, background: tab===t ? '#fff' : 'transparent', color: tab===t ? '#2554b8' : '#64748b', boxShadow: tab===t ? '0 2px 8px rgba(37,84,184,0.12)' : 'none', transition:'all 0.2s' }}>{l}</button>
        ))}
      </div>

      {msg && <div style={{ padding:'10px 16px', borderRadius:8, fontSize:13, fontWeight:600, marginBottom:16, background: msg.startsWith('✅') ? '#f0fdf4' : '#fef2f2', color: msg.startsWith('✅') ? '#16a34a' : '#dc2626', border:`1px solid ${msg.startsWith('✅') ? '#86efac' : '#fca5a5'}`, cursor:'pointer' }} onClick={() => setMsg('')}>{msg}</div>}

      {tab === 'browse' && (
        <>
          <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20 }}>
            <input className="form-control" placeholder="🔍 Search materials…" value={filters.search} onChange={e => setFilters(f=>({...f,search:e.target.value}))} style={{ width:190 }} />
            <select className="form-control" value={filters.class_name} onChange={e => setFilters(f=>({...f,class_name:e.target.value}))} style={{ width:115 }}>
              <option value="">All Classes</option>
              {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="form-control" value={filters.board} onChange={e => setFilters(f=>({...f,board:e.target.value}))} style={{ width:95 }}>
              <option value="">All Boards</option>
              {BOARDS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <input className="form-control" placeholder="Subject…" value={subjectInput}
              onChange={e => setSubjectInput(e.target.value)}
              onBlur={() => setFilters(f=>({...f,subject:subjectInput.trim()}))}
              onKeyDown={e => e.key==='Enter' && setFilters(f=>({...f,subject:subjectInput.trim()}))}
              style={{ width:120 }} />
            <select className="form-control" value={filters.material_type} onChange={e => setFilters(f=>({...f,material_type:e.target.value}))} style={{ width:155 }}>
              <option value="">All Types</option>
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={() => { setFilters({ class_name:'', board:'', subject:'', material_type:'', search:'' }); setSubjectInput(''); }} style={{ padding:'8px 14px', borderRadius:8, border:'1px solid #e2e8f0', background:'#fff', color:'#64748b', fontWeight:600, fontSize:13, cursor:'pointer' }}>Clear</button>
          </div>
          {materials.length === 0
            ? <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}><div style={{ fontSize:48 }}>📂</div><p>No materials found for these filters.</p><button onClick={() => setTab('requests')} style={{ marginTop:12, padding:'8px 18px', borderRadius:8, border:'1px solid #2554b8', background:'transparent', color:'#2554b8', fontWeight:700, cursor:'pointer' }}>Request a Material</button></div>
            : <>
                <p style={{ fontSize:13, color:'#64748b', marginBottom:14 }}>{materials.length} material{materials.length!==1?'s':''}</p>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
                  {materials.map(m => <MaterialCard key={m.id} m={m} onBookmark={toggleBookmark} onDownload={download} />)}
                </div>
              </>
          }
        </>
      )}

      {tab === 'bookmarks' && (
        bookmarks.length === 0
          ? <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}><div style={{ fontSize:48 }}>☆</div><p>No bookmarks yet. Tap ☆ on any material to save it.</p></div>
          : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
              {bookmarks.map(m => <MaterialCard key={m.id} m={m} onBookmark={toggleBookmark} onDownload={download} />)}
            </div>
      )}

      {tab === 'recent' && (
        recent.length === 0
          ? <div style={{ textAlign:'center', padding:60, color:'#94a3b8' }}><div style={{ fontSize:48 }}>🕐</div><p>No recently viewed materials.</p></div>
          : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
              {recent.map(m => <MaterialCard key={m.id} m={m} onBookmark={toggleBookmark} onDownload={download} />)}
            </div>
      )}

      {tab === 'requests' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:24, border:'1.5px solid #e2e8f0' }}>
            <h3 style={{ fontWeight:800, fontSize:15, marginBottom:18, color:'#0f1f45' }}>📝 Request a Material</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div><label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:4 }}>WHAT DO YOU NEED? *</label>
                <input className="form-control" value={reqForm.title} onChange={e => setReqForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Class 10 Maths Previous Year Papers" maxLength={200} /></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div><label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:4 }}>CLASS</label>
                  <select className="form-control" value={reqForm.class_name} onChange={e => setReqForm(f=>({...f,class_name:e.target.value}))}>
                    <option value="">—</option>{CLASSES.map(c=><option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div><label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:4 }}>BOARD</label>
                  <select className="form-control" value={reqForm.board} onChange={e => setReqForm(f=>({...f,board:e.target.value}))}>
                    <option value="">—</option>{BOARDS.map(b=><option key={b} value={b}>{b}</option>)}
                  </select></div>
              </div>
              <div><label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:4 }}>SUBJECT</label>
                <input className="form-control" value={reqForm.subject} onChange={e => setReqForm(f=>({...f,subject:e.target.value}))} placeholder="e.g. Physics" maxLength={100} /></div>
              <div><label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:4 }}>MATERIAL TYPE</label>
                <select className="form-control" value={reqForm.material_type} onChange={e => setReqForm(f=>({...f,material_type:e.target.value}))}>
                  <option value="">—</option>{TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select></div>
              <div><label style={{ fontSize:12, fontWeight:700, color:'#64748b', display:'block', marginBottom:4 }}>ADDITIONAL DETAILS</label>
                <textarea className="form-control" rows={3} value={reqForm.message} onChange={e => setReqForm(f=>({...f,message:e.target.value}))} placeholder="Any specific chapter, year, topic…" maxLength={1000} /></div>
              <button onClick={submitRequest} disabled={reqLoading} style={{ padding:'11px', borderRadius:10, border:'none', background: reqLoading ? '#94a3b8' : 'linear-gradient(135deg,#2554b8,#3b82f6)', color:'#fff', fontWeight:700, fontSize:14, cursor: reqLoading ? 'not-allowed' : 'pointer' }}>
                {reqLoading ? 'Submitting…' : '📤 Submit Request'}
              </button>
            </div>
          </div>
          <div>
            <h3 style={{ fontWeight:700, fontSize:15, marginBottom:14, color:'#0f1f45' }}>My Past Requests</h3>
            {requests.length === 0
              ? <div style={{ color:'#94a3b8', fontSize:13 }}>No requests yet.</div>
              : <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {requests.map(r => (
                    <div key={r.id} style={{ border:'1.5px solid #e2e8f0', borderRadius:10, padding:'12px 14px', background:'#fff' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                        <span style={{ fontWeight:700, fontSize:13 }}>{r.title}</span>
                        <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:20, background: r.status==='fulfilled'?'#dcfce7':r.status==='rejected'?'#fee2e2':'#fef9c3', color: STATUS_COLOR[r.status] }}>{r.status}</span>
                      </div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>{[r.class_name,r.board,r.subject,r.material_type].filter(Boolean).join(' · ')} · {new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
            }
          </div>
        </div>
      )}
    </div>
  );
}

export { AdminMaterials, UserMaterials };
